<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import appConfig from "@/app.config";
import Customers from '@/services/Customers';
import Ticketing from '@/services/Ticketing';
import General from '@/services/General';
import VueBootstrapTypeahead from 'vue-bootstrap-typeahead';
import _ from 'underscore';

export default {
  components: { Layout, PageHeader, VueBootstrapTypeahead },
  page: {
    title: "Ticketing Data",
    meta: [
      {
        name: "description",
        content: appConfig.description,
      },
    ],
  },
  watch: {
    'filterInput.email': _.debounce(function(search) { this.getCustomersAutocomplete(search, 'email') }, 900),
    'filterInput.provider_ticketing_id': _.debounce(function(search) { this.getCustomersAutocomplete(search, 'provider_ticketing_id') }, 900),
  },
  data() {
    return {
      title: "Ticketing Data",
      items: [
        {
          text: "Ticketing",
          href: "/ticketing",
        },
        {
          text: "Ticketing Data",
          active: true,
        },
      ],
      filterInput: {
        email: '',
        provider_ticketing_id: '',
      },
      customerData: [],
      tableData: [],
      ticketingData: null,
      isBusy: false,
      error: null,
    };
  },
  computed: {
    customerRow() {
      return Array.isArray(this.tableData) && this.tableData.length
        ? this.tableData[0]
        : null;
    },
    ticketingRow() {
      return this.ticketingData && this.ticketingData.data
        ? this.ticketingData.data
        : null;
    },
    ticketingOriginal() {
      return this.ticketingRow && this.ticketingRow.providerOriginalData
        ? this.ticketingRow.providerOriginalData
        : null;
    },

    // Normalized, comparable values
    crmFullName() {
      if (!this.customerRow) return '';
      const parts = [
        this.customerRow.first_name,
        this.customerRow.middle_name,
        this.customerRow.last_name,
      ].filter(Boolean);
      return parts.join(' ');
    },
    ticketingFullName() {
      if (!this.ticketingOriginal) return '';
      const parts = [
        this.ticketingOriginal.FirstName,
        this.ticketingOriginal.MiddleName,
        this.ticketingOriginal.LastName,
      ].filter(Boolean);
      return parts.join(' ');
    },
    crmAddress() {
      if (!this.customerRow) return '';
      const parts = [
        this.customerRow.address_line1,
        this.customerRow.address_line2,
        this.customerRow.address_line3,
        this.customerRow.address_city,
        this.customerRow.address_postal,
        this.customerRow.address_country,
      ].filter(Boolean);
      return parts.join(' ');
    },
    ticketingAddress() {
      if (!this.ticketingOriginal) return '';
      const parts = [
        this.ticketingOriginal.Street,
        this.ticketingOriginal.StreetNr,
        this.ticketingOriginal.City,
        this.ticketingOriginal.PostalCode,
        this.ticketingRow.countryIso2,
      ].filter(Boolean);
      return parts.join(' ');
    },
    // Difference flags
    fullNameDifferent() {
      if (!this.customerRow || !this.ticketingOriginal) {
        // If we don't have both sides, don't mark as different
        return false;
      }
      return this.areDifferent(this.crmFullName, this.ticketingFullName);
    },
    emailDifferent() {
      if (!this.customerRow || !this.ticketingOriginal) {
        return false;
      }
      const crm = this.customerRow ? this.customerRow.email : '';
      const tkt = this.ticketingOriginal ? this.ticketingOriginal.Email : '';
      return this.areDifferent(crm, tkt);
    },
    dobDifferent() {
      if (!this.customerRow || !this.ticketingOriginal) {
        return false;
      }
      const crm = this.customerRow ? this.customerRow.date_of_birth : '';
      let tkt = this.ticketingOriginal ? this.ticketingOriginal.DateOfBirth : '';
      if (tkt && typeof tkt === 'string' && tkt.includes('T')) {
        tkt = tkt.split('T')[0];
      }
      return this.areDifferent(crm, tkt);
    },
    genderDifferent() {
      if (!this.customerRow || !this.ticketingOriginal) {
        return false;
      }
      const crm = this.customerRow ? this.customerRow.gender : '';
      const tkt = this.ticketingOriginal ? (this.ticketingOriginal.gender || '') : '';
      return this.areDifferent(crm, tkt);
    },
    phoneDifferent() {
      if (!this.customerRow || !this.ticketingOriginal) {
        return false;
      }
      const crm = this.customerRow ? this.customerRow.phone_number : '';
      const tkt = this.ticketingOriginal ? this.ticketingOriginal.TelephoneNr : '';
      return this.areDifferent(crm, tkt);
    },
    addressDifferent() {
      if (!this.customerRow || !this.ticketingOriginal) {
        return false;
      }
      return this.areDifferent(this.crmAddress, this.ticketingAddress);
    },
    ticketingIdDifferent() {
      if (!this.customerRow || !this.ticketingOriginal) {
        return false;
      }
      const crm = this.customerRow ? this.customerRow.ticketing_provider_id : '';
      const tkt = this.ticketingOriginal ? this.ticketingOriginal.CustomerNumber : '';
      return this.areDifferent(crm, tkt);
    },
    ssoDifferent() {
      if (!this.customerRow || !this.ticketingOriginal) {
        return false;
      }
      const crm = this.customerRow ? this.customerRow.customer_id : '';
      const tkt = this.ticketingOriginal ? this.ticketingOriginal.SSOId : '';
      return this.areDifferent(crm, tkt);
    },
  },
  methods: {
    async getCustomersAutocomplete(query, inputTypeName) {
      this.customerData = [];

      if (query.trim().length > 0) {
        let column = '';
        let limit = 1000;
        let table = '';
        let select = [];

        if (inputTypeName === 'email') {
          column = 'email';
          table = 'crm_user';
          select = ['email'];
        } else if (inputTypeName === 'provider_ticketing_id') {
          column = 'CustomerNumber';
          table = 'crm_tymes4';
          select = ['CustomerNumber'];
        }

        const payload = {
          table,
          column,
          filter: query,
          limit,
          select
        };

        try {
          const response = await General.autocomplete(payload);
          const rawData = Array.isArray(response.data.data) ? response.data.data : [];

          const suggestions = rawData.map(item => {
            if (column === 'email') return { text: item.email };
            if (column === 'CustomerNumber') return { text: item.CustomerNumber };
            return null;
          }).filter(Boolean);

          this.customerData = suggestions.filter(
            (v, i, a) => a.findIndex(t => t.text === v.text) === i
          );
        } catch (err) {
          console.error(err);
        }
      }
    },

    async search() {
      this.isBusy = true;
      this.tableData = [];
      this.ticketingData = [];
      this.error = null;

      const email = this.filterInput.email.trim();
      const ticketingId = this.filterInput.provider_ticketing_id.trim();

      try {
        // Build filter string for customers endpoint
        let filterString = '';
        if (email) {
          filterString += `email=${encodeURIComponent(email)}`;
        }
        if (ticketingId) {
          filterString += filterString ? '&' : '';
          filterString += `provider_ticketing_id=${encodeURIComponent(ticketingId)}`;
        }

        // Call both endpoints in parallel
        const [customerResponse, ticketingResponse] = await Promise.all([
          filterString ? Customers.getCustomersByFilter(filterString) : Promise.resolve({ data: { data: [] } }),
          (email || ticketingId) ? Ticketing.getUserByCrmId(email || ticketingId) : Promise.resolve({ data: { data: [] } })
        ]);

        this.tableData = customerResponse.data.data || [];
        
        // Store full ticketing response
        this.ticketingData = ticketingResponse.data || null;

      } catch (error) {
        this.error = error.response?.data?.error || 'An error occurred';
        console.error(error);
      } finally {
        this.isBusy = false;
      }
    },

    resetFilters() {
      this.filterInput.email = '';
      this.filterInput.provider_ticketing_id = '';
      this.tableData = [];
      this.ticketingData = null;
      this.error = null;
    },

    areDifferent(a, b) {
      const norm = (val) =>
        (val === null || val === undefined)
          ? ''
          : String(val).trim().toLowerCase();
      return norm(a) !== norm(b);
    },
  },
  middleware: "authentication",
};
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />
    
    <!-- Filters Card -->
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <h4 class="card-title mb-4">Search Filters</h4>
            <div class="row">
              <div class="col-sm-12 col-md-4">
                <b-form-group label="Email" label-for="formrow-email-input" class="mb-3">
                  <vue-bootstrap-typeahead 
                    id="formrow-email-input" 
                    ref="emailAutocomplete"
                    v-model.trim="filterInput.email"
                    :data="customerData"
                    :serializer="s => s.text"
                    placeholder="Enter email..."
                  />
                </b-form-group>
              </div>
              <div class="col-sm-12 col-md-4">
                <b-form-group label="Ticketing Customer Number" label-for="formrow-ticketing-id-input" class="mb-3">
                  <vue-bootstrap-typeahead 
                    id="formrow-ticketing-id-input" 
                    ref="ticketingIdAutocomplete"
                    v-model.trim="filterInput.provider_ticketing_id"
                    :data="customerData"
                    :serializer="s => s.text"
                    placeholder="Enter customer number..."
                  />
                </b-form-group>
              </div>
              <div class="col-sm-12 col-md-4 d-flex align-items-end mb-3">
                <button type="button" class="btn btn-success me-2" @click="search">
                  <i class="mdi mdi-magnify me-1"></i> Search
                </button>
                <button type="button" class="btn btn-primary" @click="resetFilters">
                  <i class="mdi mdi-refresh me-1"></i> Reset
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Split Screen Results -->
    <div class="row">
      <!-- Left Side - Customer Data -->
      <div class="col-md-6">
        <div class="card">
          <div class="card-body">
            <h4 class="card-title mb-4">Customer Data</h4>
            <div v-if="isBusy" class="text-center text-danger my-2">
              <b-spinner class="align-middle"></b-spinner>
              <strong>Loading...</strong>
            </div>
            <div v-else-if="!customerRow" class="text-muted">
              No customer data found
            </div>
            <div v-else class="table-responsive">
              <table class="table table-sm table-bordered mb-0">
                <tbody>
                  <tr>
                    <th class="w-50">SSO Id</th>
                    <td>{{ customerRow.customer_id }}</td>
                  </tr>
                  <tr>
                    <th>Full Name</th>
                    <td :class="{ 'text-danger': fullNameDifferent }">
                      {{ customerRow.first_name }}
                      <span v-if="customerRow.middle_name"> {{ customerRow.middle_name }}</span>
                      {{ customerRow.last_name }}
                    </td>
                  </tr>
                  <tr>
                    <th>Email</th>
                    <td :class="{ 'text-danger': emailDifferent }">
                      {{ customerRow.email }}
                    </td>
                  </tr>
                  <tr>
                    <th>Date of Birth</th>
                    <td :class="{ 'text-danger': dobDifferent }">
                      {{ customerRow.date_of_birth }}
                    </td>
                  </tr>
                  <tr>
                    <th>Gender</th>
                    <td :class="{ 'text-danger': genderDifferent }">
                      {{ customerRow.gender }}
                    </td>
                  </tr>
                  <tr>
                    <th>Address</th>
                    <td :class="{ 'text-danger': addressDifferent }">
                      {{ customerRow.address_line1 }}
                      <span v-if="customerRow.address_line2"> {{ customerRow.address_line2 }}</span>
                      <span v-if="customerRow.address_line3"> {{ customerRow.address_line3 }}</span>,
                      {{ customerRow.address_city }}
                      {{ customerRow.address_postal }},
                      {{ customerRow.address_country }}
                    </td>
                  </tr>
                  <tr>
                    <th>Phone Number</th>
                    <td :class="{ 'text-danger': phoneDifferent }">
                      {{ customerRow.phone_number }}
                    </td>
                  </tr>
                  <tr>
                    <th>Ticketing Customer Number</th>
                    <td :class="{ 'text-danger': ticketingIdDifferent }">
                      {{ customerRow.ticketing_provider_id }}
                    </td>
                  </tr>
                  <tr>
                    <th>Status</th>
                    <td>{{ customerRow.status }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <!-- Right Side - Ticketing Data -->
      <div class="col-md-6">
        <div class="card">
          <div class="card-body">
            <h4 class="card-title mb-4">Ticketing Data</h4>
            <div v-if="isBusy" class="text-center text-danger my-2">
              <b-spinner class="align-middle"></b-spinner>
              <strong>Loading...</strong>
            </div>
            <div v-else-if="!ticketingRow || !ticketingOriginal" class="text-muted">
              No ticketing data found
            </div>
            <div v-else class="table-responsive">
              <table class="table table-sm table-bordered mb-0">
                <tbody>
                  <tr>
                    <th>SSO Id</th>
                    <td :class="{ 'text-danger': ssoDifferent }">
                      {{ ticketingOriginal.SSOId }}
                    </td>
                  </tr>
                  <tr>
                    <th>Full Name</th>
                    <td :class="{ 'text-danger': fullNameDifferent }">
                      {{ ticketingOriginal.FirstName }}
                      <span v-if="ticketingOriginal.MiddleName"> {{ ticketingOriginal.MiddleName }}</span>
                      {{ ticketingOriginal.LastName }}
                    </td>
                  </tr>
                  <tr>
                    <th>Email</th>
                    <td :class="{ 'text-danger': emailDifferent }">
                      {{ ticketingOriginal.Email }}
                    </td>
                  </tr>
                  <tr>
                    <th>Date Of Birth</th>
                    <td :class="{ 'text-danger': dobDifferent }">
                         {{ ticketingOriginal.DateOfBirth && ticketingOriginal.DateOfBirth.split('T')[0] }}
                    </td>
                  </tr>
                  <tr>
                    <th>Gender</th>
                    <td :class="{ 'text-danger': genderDifferent }">
                      {{ ticketingOriginal.gender }}
                    </td>
                  </tr>
                  <tr>
                    <th>Address</th>
                    <td :class="{ 'text-danger': addressDifferent }">
                      {{ ticketingOriginal.Street }}
                      <span v-if="ticketingOriginal.StreetNr"> {{ ticketingOriginal.StreetNr }}</span>,
                      {{ ticketingOriginal.City }}
                      {{ ticketingOriginal.PostalCode }},
                      {{ ticketingRow.countryIso2 }}
                    </td>
                  </tr>
                  <tr>
                    <th>Phone Number</th>
                    <td :class="{ 'text-danger': phoneDifferent }">
                      {{ ticketingOriginal.TelephoneNr }}
                    </td>
                  </tr>
                  <tr>
                    <th>Ticketing Customer Number</th>
                    <td :class="{ 'text-danger': ticketingIdDifferent }">
                      {{ ticketingOriginal.CustomerNumber }}
                    </td>
                  </tr>
                  <tr>
                    <th class="w-50">Provider</th>
                    <td>{{ ticketingRow.provider }}</td>
                  </tr>
                  <tr>
                    <th>Customer Type</th>
                    <td :class="{ 'text-danger': emailDifferent && ticketingOriginal.CustomerType === 1 }">{{ ticketingOriginal.CustomerType }}</td>
                  </tr>
                  <tr>
                    <th>Sales Group Id</th>
                    <td :class="{ 'text-danger': emailDifferent && ticketingOriginal.CustomerType === 1 }">{{ ticketingOriginal.SalesGroupId }}</td>
                  </tr>
                 
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>

  </Layout>
</template>

